import { FunctionComponent, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Collapse,
  Tag,
  Text,
  getTheme,
} from '@conexasaude/conexa-hero'
import { useStyles } from './styles'
import { capitalize } from '@/utils/fnUtils'
import { RenderIf } from '@/components/RenderIf'
import { UserCardTypes } from './types'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { BUTTON_TEXT_MAP, ICON_MAP, INTERACTION_TEXT_MAP } from './constants'
import { AnalyticEvent } from '@/modules/Signup/types/en-us'
import { SEND_TO } from '@/modules/Signup/constants'

const UserCard: FunctionComponent<UserCardTypes> = ({
  onActionClick,
  onLinkClick,
  clinicUser,
  isLoading,
}) => {
  const styles = useStyles()
  const theme = getTheme()
  const { logAnalyticsEvent } = useFirebaseContext()
  const getEnabledServices = () => {
    if (!clinicUser?.clinicSpecialites) return null
    const enabledServices = []
    let immediate = false

    for (const key in clinicUser?.clinicSpecialites) {
      const service = clinicUser?.clinicSpecialites[key]

      if (service?.habilitado && service?.atendimentoImediato && !immediate) {
        enabledServices.push('Atendimento imediato')
        immediate = true
      }
      if (service?.habilitado) enabledServices?.push(service?.descricaoGeral)
    }
    return enabledServices
  }

  const dependentFullList = useMemo(() => {
    return clinicUser?.adultDependents?.concat(clinicUser?.childrenDependents)
  }, [clinicUser?.adultDependents, clinicUser?.childrenDependents])

  const uniqueById = dependents => {
    const seenIds = new Set()
    return dependents?.filter(dependent => {
      if (!seenIds.has(dependent.idUsuario)) {
        seenIds.add(dependent.idUsuario)
        return true
      }
      return false
    })
  }

  const handleActivateAccountAndAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      logAnalyticsEvent(eventDefinitions?.eventName, {
        interaction: eventDefinitions?.interaction,
        options: {
          sendTo: SEND_TO,
        },
      })
      onActionClick()
    },
    [logAnalyticsEvent, onActionClick],
  )

  return (
    <Box background="white">
      <div css={styles.card}>
        <div css={styles.cardHeader}>
          <Text variant="subheading" color={theme.colors.brand.primary[500]}>
            {clinicUser?.clinic?.name}
          </Text>
          <div>
            <div css={styles.tagWrapper}>
              <Tag
                size="small"
                variant={clinicUser?.flagIsActive ? 'success' : 'error'}
              >
                {clinicUser?.flagIsActive ? 'Ativa' : 'Inativa'}
              </Tag>
            </div>
          </div>
        </div>
        <RenderIf condition={clinicUser?.flagIsActive}>
          <div>
            <Text
              css={styles.nameWrapperTitle}
              renderAs="span"
              variant="paragraphSmall"
              color={theme.colors.brand.primary[500]}
            >
              {capitalize(clinicUser?.holder?.toLowerCase())}:{' '}
            </Text>
            <Text
              renderAs="span"
              color={theme.colors.brand.primary[500]}
              variant="paragraphSmall"
            >
              {' '}
              {capitalize(clinicUser?.name?.toLowerCase()) ||
                capitalize(clinicUser?.socialNameOrName?.toLowerCase()) ||
                capitalize(clinicUser?.patientName?.toLowerCase())}
            </Text>
          </div>
          <RenderIf condition={clinicUser?.holder === 'DEPENDENTE'}>
            <div>
              <Text
                css={styles.nameWrapperTitle}
                renderAs="span"
                variant="paragraphSmall"
                color={theme.colors.brand.primary[500]}
              >
                Titular:
              </Text>
              <Text
                renderAs="span"
                color={theme.colors.brand.primary[500]}
                variant="paragraphSmall"
              >
                {' '}
                {capitalize(clinicUser?.responsable?.name?.toLowerCase())}
              </Text>
            </div>
          </RenderIf>
          <RenderIf condition={!!dependentFullList?.length}>
            <Text
              css={styles.link}
              onClick={onLinkClick}
              variant="linkUnderline"
            >
              Ver dependentes ({uniqueById(dependentFullList)?.length})
            </Text>
          </RenderIf>
        </RenderIf>
        <RenderIf condition={!clinicUser?.flagIsActive}>
          <Text renderAs="p" variant="paragraphSmall">
            <Text
              css={styles.nameWrapperTitle}
              renderAs="span"
              variant="paragraphSmall"
              color={theme.colors.brand.primary[500]}
            >
              {`${capitalize(clinicUser?.holder?.toLowerCase())}:`}
            </Text>
            <Text
              renderAs="span"
              color={theme.colors.brand.primary[500]}
              variant="paragraphSmall"
            >
              {' '}
              {capitalize(clinicUser?.name?.toLowerCase()) ||
                capitalize(clinicUser?.socialNameOrName?.toLowerCase()) ||
                capitalize(clinicUser?.patientName?.toLowerCase())}
            </Text>

            <RenderIf condition={!!dependentFullList?.length}>
              {` | Dependentes (${dependentFullList?.length})`}
            </RenderIf>
          </Text>
          <RenderIf condition={clinicUser?.holder === 'DEPENDENTE'}>
            <div>
              <Text
                css={styles.nameWrapperTitle}
                renderAs="span"
                variant="paragraphSmall"
                color={theme.colors.brand.primary[500]}
              >
                Titular:
              </Text>
              <Text
                renderAs="span"
                color={theme.colors.brand.primary[500]}
                variant="paragraphSmall"
              >
                {' '}
                {capitalize(clinicUser?.responsable?.name?.toLowerCase())}
              </Text>
            </div>
          </RenderIf>
        </RenderIf>
        <hr />
        <RenderIf condition={!!getEnabledServices().length}>
          <div css={styles.specialtiesWrapper}>
            <Collapse title="Especialidades disponíveis" variant="transparent">
              <Text
                variant="paragraphSmall"
                renderAs="span"
                color={theme.colors.neutral[300]}
              >
                {getEnabledServices().join(' • ')}
              </Text>
            </Collapse>
          </div>
        </RenderIf>
        <div css={styles.cardFooter}>
          <RenderIf condition={clinicUser?.flagIsActive}>
            <Button
              busy={isLoading}
              onClick={() =>
                handleActivateAccountAndAnalyticsEvent({
                  eventName: 'at_patient_multiprofile',
                  interaction: 'access_account_click',
                })
              }
              variant="secondary"
              fullWidth
            >
              Acessar conta
            </Button>
          </RenderIf>
          <RenderIf condition={!clinicUser?.flagIsActive}>
            <Button
              onClick={() =>
                handleActivateAccountAndAnalyticsEvent({
                  eventName: 'at_patient_multiprofile',
                  interaction: INTERACTION_TEXT_MAP[clinicUser?.holder],
                })
              }
              leftIcon={ICON_MAP[clinicUser?.holder]}
              variant="tertiary"
            >
              {BUTTON_TEXT_MAP[clinicUser?.holder]}
            </Button>
          </RenderIf>
        </div>
      </div>
    </Box>
  )
}

export default UserCard
